import React, { useMemo, useState } from "react";
import { Button, Switch } from "@elaniintc/goes-design-system";
import type { CalculationElement } from "../types";
import { nodeVariableToGroups, type NodeVariable } from "../../../types";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";

interface BooleanOperationInputProps {
  variables: NodeVariable[];
  /** Tipo de operación booleana */
  operation: string;
  /** Elementos para gestionar los valores */
  elements: CalculationElement[];
  /** Callback para actualizar elementos */
  onElementsChange: (elements: CalculationElement[]) => void;
  /** Grupo de elementos */
  group?:
    | "down-left"
    | "down"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left";
}

/**
 * Componente para renderizar inputs específicos de operaciones booleanas
 */
export const BooleanOperationInput: React.FC<BooleanOperationInputProps> = ({
  operation,
  elements,
  onElementsChange,
  variables,
}) => {
  const [variableSearch, setVariableSearch] = useState("");
  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    if (variableSearch) {
      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (item.label as string)
                .toLowerCase()
                .includes(variableSearch.toLowerCase()) ||
              group.groupLabels.some((label) =>
                label.toLowerCase().includes(variableSearch.toLowerCase()),
              ),
          ),
        }))
        .filter((group) => group.items.length > 0);
    }
    return groups;
  }, [variables, variableSearch]);

  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();
  const onVariableSelection = (variableId: string, groupId: string) => {
    const group = variableGroups.find((g) => g.groupId === groupId);
    const variable = group?.items.find((item) => item.value === variableId);
    setSelectedVariable(variable);
    setSelectedGroup(group);
    onElementsChange([
      {
        id: variableId,
        type: "var",
        value: variableId,
      },
    ]);
    //handleVariableChange(variableId);
  };

  // No input needed for 'not', 'toNumber', 'toString'
  if (["not", "toNumber", "toString"].includes(operation)) {
    return null;
  }

  // Boolean operations that need input: and, or, xor, xnor
  return (
    <>
      <Button
        variant="quiet"
        group="down-left"
        className="h-full px-3"
        style={{ minHeight: 40 }}
        onClick={() => {
          const currentType = elements[0]?.type || "inp";
          const newType = currentType === "inp" ? "var" : "inp";
          onElementsChange([
            {
              id: elements[0]?.id || Math.random().toString(36).substr(2, 9),
              type: newType,
              value: "",
            },
          ]);
        }}
      >
        {elements[0]?.type || "inp"}
      </Button>
      {!elements[0] || elements[0].type === "inp" ? (
        <div className="flex-1 flex items-center bg-neutral-background px-2 rounded-br-md border-b border-r border-neutral-silent">
          <Switch
            checked={elements[0]?.value === "true"}
            onCheckedChange={(checked) => {
              onElementsChange([
                {
                  id:
                    elements[0]?.id || Math.random().toString(36).substr(2, 9),
                  type: "inp",
                  value: checked ? "true" : "false",
                },
              ]);
            }}
            className="mr-2"
          />
          <span className="text-body-quiet">
            {elements[0]?.value === "true" ? "True" : "False"}
          </span>
        </div>
      ) : (
        <DropdownList
          group="down-right"
          options={variableGroups}
          selectedValue={_selectedVariable}
          selectedGroup={_selectedGroup}
          toggleSelection={(variable, group) => {
            onVariableSelection(variable.value, group?.groupId || "");
          }}
          widthClassName="flex-1"
          placeholder="Elegir variable"
          search={{
            placeholder: "Buscar variable",
            onSearch: (query) => {
              setVariableSearch(query);
            },
            query: variableSearch,
          }}
        />
        // <Select
        //   value={elements[0]?.value}
        //   onValueChange={(value) => onElementsChange([{ id: elements[0]?.id || Math.random().toString(36).substr(2, 9), type: 'var', value }])}
        // >
        //   <SelectTrigger className="w-full" group={'down-right'}>
        //     <div className="flex items-center gap-2">
        //       <Icon name="toggle-large-on" type="far" className="text-body" />
        //       <SelectValue placeholder="Elige una variable" />
        //     </div>
        //   </SelectTrigger>
        //   <SelectContent>
        //     {variableOptions.filter(v => v.type === 'bool').map((option) => (
        //       <SelectItem key={option.value} value={option.value}>
        //         <div className="flex items-center gap-2">
        //           <Icon name={option.icon} type="far" className="text-body" />
        //           <span className="text-body font-semibold">{option.label}</span>
        //           <span className="text-body-quiet">{option.type}</span>
        //         </div>
        //       </SelectItem>
        //     ))}
        //   </SelectContent>
        // </Select>
      )}
    </>
  );
};
